/* DC Show Cars block. Desktop: the block pins and ScrollTrigger scrubs the plate track
   sideways, each plate settles (image scale, HUD lift) as it crosses the viewport, the
   counter and the red lane line follow the same progress. Phones, tablets and reduced
   motion: no pin, the stacked plates reveal once each (IntersectionObserver).

   init(root) is idempotent, everything lives in a gsap.matchMedia so the breakpoint
   switch and the theme editor's section reload (shopify:section:load) revert cleanly.
   Resting states are set in JS, never in CSS: without JS the block is a complete,
   natively scrollable strip (see dc-builds.css). */
(() => {
  const DESKTOP = '(min-width: 1024px)';
  const forced = () => !!window.__dcForceMotion;
  const reducedQuery = '(prefers-reduced-motion: reduce)';
  const pad2 = (n) => String(n).padStart(2, '0');

  const parts = (root) => ({
    track: root.querySelector('[data-dc-track]'),
    viewport: root.querySelector('[data-dc-viewport]'),
    pinEl: root.querySelector('.dc-builds__pin'),
    plates: [...root.querySelectorAll('[data-dc-plate]')],
    cars: [...root.querySelectorAll('[data-dc-plate]:not([data-dc-end])')],
    cur: root.querySelector('[data-dc-cur]'),
    lane: root.querySelector('[data-dc-lane]'),
    head: [...root.querySelectorAll('[data-dc-head]')]
  });

  const teardown = (root) => {
    if (root.__dcBuildsMM) { root.__dcBuildsMM.revert(); root.__dcBuildsMM = null; }
    if (root.__dcBuildsIO) { root.__dcBuildsIO.disconnect(); root.__dcBuildsIO = null; }
    root.classList.remove('is-pinned');
  };

  const init = (root) => {
    teardown(root);
    const g = window.gsap;
    const ST = window.ScrollTrigger;
    const p = parts(root);
    if (!g || !ST || !p.track || !p.plates.length) return;
    const pinWanted = root.dataset.pin !== 'false';

    const mm = g.matchMedia();
    root.__dcBuildsMM = mm;

    /* Desktop line-up. Reduced motion falls through to the stacked branch unless the
       page has forced motion on (dc-event.js sets window.__dcForceMotion). */
    const desktopCond = forced() ? DESKTOP : `${DESKTOP} and (prefers-reduced-motion: no-preference)`;
    mm.add(desktopCond, () => {
      if (!pinWanted) return stacked(root, p, g);
      root.classList.add('is-pinned');
      const dist = () => Math.max(0, p.track.scrollWidth - p.viewport.clientWidth);
      const tween = g.to(p.track, { x: () => -dist(), ease: 'none' });
      /* Pin the inner wrapper, not the section: ScrollTrigger wraps the pinned element in
         a spacer div, and the page rail (dc-event.js) finds chapters as direct children
         of the .dc-section wrapper, so pinning the section itself would drop SHOW CARS
         from the rail. */
      const main = ST.create({
        trigger: root,
        start: 'top top',
        end: () => '+=' + dist(),
        pin: p.pinEl || root,
        anticipatePin: 1,
        scrub: 0.6,
        invalidateOnRefresh: true,
        animation: tween,
        onUpdate: (self) => {
          const n = p.cars.length;
          if (p.cur && n) {
            const i = Math.min(n - 1, Math.round(self.progress * (n - 1 + 0.35)));
            const txt = pad2(i + 1);
            if (p.cur.textContent !== txt) p.cur.textContent = txt;
          }
          if (p.lane) g.set(p.lane, { scaleX: self.progress });
        }
      });

      /* Each plate settles as it crosses the viewport: image from 1.08 to 1, HUD rises. */
      p.plates.forEach((plate) => {
        const img = plate.querySelector('.dc-builds__img');
        const hud = plate.querySelectorAll('.dc-builds__bottom, .dc-builds__top');
        if (img) g.fromTo(img, { scale: 1.08 }, {
          scale: 1, ease: 'none',
          scrollTrigger: { trigger: plate, containerAnimation: tween, start: 'left 95%', end: 'left 35%', scrub: true }
        });
        if (hud.length) g.fromTo(hud, { y: 22, autoAlpha: 0.35 }, {
          y: 0, autoAlpha: 1, ease: 'none',
          scrollTrigger: { trigger: plate, containerAnimation: tween, start: 'left 90%', end: 'left 45%', scrub: true }
        });
      });

      /* Head lines rise once when the block arrives. */
      if (p.head.length) {
        g.set(p.head, { y: 18, autoAlpha: 0 });
        g.to(p.head, { y: 0, autoAlpha: 1, duration: .8, ease: 'power3.out', stagger: .08,
          scrollTrigger: { trigger: root, start: 'top 70%', once: true } });
      }

      /* Plate widths are svh based, so a late image or font changes the track length. */
      const imgs = [...root.querySelectorAll('img')].filter((i) => !i.complete);
      imgs.forEach((i) => i.addEventListener('load', () => ST.refresh(), { once: true }));
      return () => { main.kill(); };
    });

    /* Everything else: stacked cards, one reveal each. */
    const stackedCond = forced() ? `not all and ${DESKTOP}` : `not all and ${DESKTOP}, ${reducedQuery}`;
    mm.add(stackedCond, () => stacked(root, p, g));
  };

  const stacked = (root, p, g) => {
    if (root.classList.contains('is-pinned')) root.classList.remove('is-pinned');
    const targets = [...p.head, ...p.plates];
    if (!targets.length) return;
    g.set(targets, { y: 16, autoAlpha: 0 });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        g.to(e.target, { y: 0, autoAlpha: 1, duration: .7, ease: 'power3.out' });
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    targets.forEach((t) => io.observe(t));
    root.__dcBuildsIO = io;
    return () => { io.disconnect(); root.__dcBuildsIO = null; g.set(targets, { clearProps: 'all' }); };
  };

  const boot = () => document.querySelectorAll('[data-dc-builds]').forEach(init);
  if (window.__dcEvent) boot(); else document.addEventListener('dc:event:ready', boot, { once: true });
  /* If the event runtime never arrives (script blocked), the CSS layout stands as is. */

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => { const r = e.target.querySelector('[data-dc-builds]'); if (r) init(r); });
    document.addEventListener('shopify:section:unload', (e) => { const r = e.target.querySelector('[data-dc-builds]'); if (r) teardown(r); });
  }
})();
